import { useEffect, useRef, useState } from 'react';

import {
  claimRecoverySlot,
  documentUrlFor,
  isTransientNavigationError,
  RECOVERY_GUARD_MS,
} from '~/utils/singleFetchResilience';

function claimDocumentUrl(targetUrl: string): string | null {
  try {
    // Concatenation keeps a protocol-relative path such as `//host/x` same-origin.
    const documentUrl = documentUrlFor(new URL(window.location.origin + targetUrl));
    // claimRecoverySlot treats a missing storage as "no loop guard", so it must never reach it.
    const storage = window.sessionStorage;
    return storage && claimRecoverySlot(storage, documentUrl, Date.now(), RECOVERY_GUARD_MS) ? documentUrl : null;
  } catch {
    return null;
  }
}

/**
 * Reloads the document at the boundary's location (without its fragment) when the error is a transient navigation
 * failure, at most once per guard window, and reports `recovering` so the caller can render a loading state instead of
 * the error page.
 */
export function useTransientErrorRecovery(
  error: unknown,
  targetUrl: string,
  onRecover: (_error: unknown, _targetUrl: string) => void
): boolean {
  // Optimistic: the slot is claimed in the effect, so a discarded render pass cannot burn it.
  const [recovering, setRecovering] = useState(
    () => typeof window !== 'undefined' && isTransientNavigationError(error)
  );
  // StrictMode runs the effect twice per mount, and the second run must not take its own claim for a denial.
  const attempted = useRef(false);

  useEffect(() => {
    if (!recovering || attempted.current) return;
    attempted.current = true;
    const documentUrl = claimDocumentUrl(targetUrl);
    if (documentUrl === null) {
      setRecovering(false);
      return;
    }
    onRecover(error, targetUrl);
    window.location.assign(window.location.origin + documentUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per boundary mount
  }, [recovering]);

  return recovering;
}
